import { useCallback, useRef, useState } from 'react'
import type { InternalMeetingConfigProblem, MinutesJobQueued } from '@ai-meeting/shared'
import { ContainerAudioStreamer } from '@/audio/ContainerAudioStreamer'
import { fetchSonioxToken } from '@/services/soniox'
import {
  buildInternalMeetingConfig,
  validateInternalMeetingConfig,
} from '@/utils/internalMeetingConfig'
import type { SttConfigContext } from '@/utils/sttConfigContext'
import { isFatalReadyModeVerdict, verifyReadyMeetingMode } from '@/utils/meetingMode'

/** Close code the server uses for a config this mode does not accept. */
const CLOSE_PROTOCOL_CONFIG = 4005

/** Normal close, which here means "the job is queued, go and poll it". */
const CLOSE_NORMAL = 1000

export type InternalMeetingState =
  | 'idle'
  | 'connecting'
  | 'recording'
  | 'paused'
  /** `stop` sent; waiting for the one event the server answers with. */
  | 'stopping'
  /** The job is ours to poll; the socket has done its part. */
  | 'queued'
  | 'error'

export interface UseInternalMeetingRecordingOptions {
  /** Meeting language. Both the STT hint and the language the minutes come back in. */
  targetLang: string
  meetingId?: string
  userId?: string
  /** Signed-in reader's name, forwarded onto the `config` frame. */
  userName?: string
  /** Forwarded onto the `config` frame; see {@link buildInternalMeetingConfig}. */
  context?: SttConfigContext | null
  /**
   * Whether the meeting bot was asked to join, and the STT engine the setup
   * picked — taken only so this mode can refuse them.
   *
   * The screen that starts the meeting carries both regardless of mode, so they
   * arrive here whether or not they are compatible. Checking them is the point:
   * the server's answer to either is a close code with no reason attached.
   */
  nativeMode?: boolean
  sttProvider?: string | null
  /**
   * When true, skip the STT socket minutes handoff — the client uploads the
   * archived recording and queues Nest `POST /minutes/audio` instead.
   */
  deliverMinutesViaClientUpload?: boolean
}

export interface InternalMeetingRecording {
  state: InternalMeetingState
  error: Error | null
  /** A config this client refused to send, named so the screen can say which rule it broke. */
  configProblem: InternalMeetingConfigProblem | null
  queued: MinutesJobQueued | null
  start: (stream: MediaStream) => Promise<void>
  stop: () => Promise<void>
  pause: () => void
  resume: () => void
  sendSpeakerMap: (speakers: Record<string, string>) => void
}

interface ServerEvent {
  event?: string
  code?: string
  message?: string
  meetingMode?: unknown
}

/**
 * Record an internal meeting: audio out, nothing back until it ends.
 *
 * Kept apart from `useProxySttRecording` rather than added to it as a branch.
 * The two share only the handshake — this one opens no realtime STT, sends a
 * compressed container instead of PCM frames, expects no transcript, translation
 * or summary, and finishes by handing over a job id to poll over HTTP. Threading
 * that through the realtime hook would put every one of those differences inside
 * a path that ordinary meetings depend on (BRAI-460).
 */
export function useInternalMeetingRecording({
  targetLang,
  meetingId,
  userId,
  userName,
  context,
  nativeMode,
  sttProvider,
  deliverMinutesViaClientUpload = false,
}: UseInternalMeetingRecordingOptions): InternalMeetingRecording {
  const [state, setState] = useState<InternalMeetingState>('idle')
  const [error, setError] = useState<Error | null>(null)
  const [configProblem, setConfigProblem] = useState<InternalMeetingConfigProblem | null>(null)
  const [queued, setQueued] = useState<MinutesJobQueued | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const streamerRef = useRef<ContainerAudioStreamer | null>(null)
  const queuedRef = useRef<MinutesJobQueued | null>(null)
  /** Set by `stop` so a late `ready` cannot start capture after the user ended. */
  const stopRequestedRef = useRef(false)

  const fail = useCallback((cause: Error) => {
    setError(cause)
    setState('error')
  }, [])

  const start = useCallback(
    async (stream: MediaStream) => {
      const input = { targetLang, meetingId, userId, userName, context, nativeMode, sttProvider }
      const problem = validateInternalMeetingConfig(input)
      if (problem) {
        // Refused here rather than by the server: it would close with 4005 and
        // no reason at all, leaving nothing to show the user but a number.
        setConfigProblem(problem)
        fail(new Error(`Internal meeting config rejected: ${problem}`))
        return
      }

      setConfigProblem(null)
      setError(null)
      setQueued(null)
      queuedRef.current = null
      stopRequestedRef.current = false

      if (deliverMinutesViaClientUpload) {
        setState('recording')
        return
      }

      setState('connecting')

      // Drop any leftover socket from a previous failed attempt so late events
      // cannot mutate this session (e.g. a delayed `ready` after retry).
      const previousSocket = wsRef.current
      if (previousSocket) {
        previousSocket.onmessage = null
        previousSocket.onerror = null
        previousSocket.onclose = null
        previousSocket.close()
        wsRef.current = null
      }
      await streamerRef.current?.stop()
      streamerRef.current = null

      let socket: WebSocket
      try {
        const { apiKey, token, wsUrl } = await fetchSonioxToken()
        const sttToken = token ?? apiKey
        if (!sttToken || !wsUrl) throw new Error('STT token response was incomplete')

        if (stopRequestedRef.current) {
          fail(new Error('Internal meeting start cancelled'))
          return
        }

        const url = new URL(wsUrl)
        url.searchParams.set('token', sttToken)
        socket = new WebSocket(url.toString())
      } catch (cause) {
        fail(cause instanceof Error ? cause : new Error(String(cause)))
        return
      }

      if (stopRequestedRef.current) {
        socket.close()
        fail(new Error('Internal meeting start cancelled'))
        return
      }

      socket.binaryType = 'arraybuffer'
      wsRef.current = socket

      socket.onmessage = (message) => {
        let event: ServerEvent
        try {
          event = JSON.parse(String(message.data)) as ServerEvent
        } catch {
          return
        }

        if (event.event === 'connected') {
          if (stopRequestedRef.current) return
          socket.send(JSON.stringify(buildInternalMeetingConfig(input)))
          return
        }

        if (event.event === 'ready') {
          if (stopRequestedRef.current) {
            socket.close()
            return
          }

          const verdict = verifyReadyMeetingMode('internal_meeting', event.meetingMode)
          if (isFatalReadyModeVerdict(verdict, 'internal_meeting')) {
            // The server is about to run this as an ordinary meeting: transcript
            // on screen, audio through an LLM. Stop before any of it is sent.
            fail(new Error(`Server did not confirm internal meeting mode (${verdict})`))
            socket.close()
            return
          }

          const streamer = new ContainerAudioStreamer()
          streamerRef.current = streamer
          streamer.start(stream, (chunk) => {
            if (socket.readyState === WebSocket.OPEN) socket.send(chunk)
          })
          setState('recording')
          return
        }

        if (event.event === 'meeting-minutes.queued') {
          const job = event as unknown as MinutesJobQueued
          queuedRef.current = job
          setQueued(job)
          setState('queued')
          return
        }

        // Both of these mean "not in this mode". They do carry a reason, unlike
        // the 4005 close, and both are dead ends — stop waiting on them.
        if (event.event === 'meeting-minutes.error' || event.event === 'language.update.error') {
          fail(new Error(event.message || `${event.event} (${event.code ?? 'unknown'})`))
        }
      }

      socket.onerror = () => {
        if (queuedRef.current) return
        fail(new Error('Internal meeting socket failed'))
      }

      socket.onclose = (closeEvent) => {
        wsRef.current = null
        if (queuedRef.current) return

        if (closeEvent.code === CLOSE_PROTOCOL_CONFIG) {
          // Nothing on the wire says which rule was broken, and the local check
          // above already passed — so this is a rule the client does not know.
          fail(new Error(`Server rejected the internal meeting config (close ${CLOSE_PROTOCOL_CONFIG})`))
          return
        }

        if (closeEvent.code !== CLOSE_NORMAL) {
          fail(new Error(`Internal meeting socket closed (${closeEvent.code})`))
          return
        }

        // Normal close without `meeting-minutes.queued` — e.g. stop while the
        // socket is open but before a job exists — must not leave `stopping`.
        if (!queuedRef.current) {
          fail(
            new Error(
              stopRequestedRef.current
                ? 'Internal meeting ended without a minutes job'
                : 'Internal meeting socket closed before minutes were queued'
            )
          )
        }
      }
    },
    [
      targetLang,
      meetingId,
      userId,
      userName,
      context,
      nativeMode,
      sttProvider,
      deliverMinutesViaClientUpload,
      fail,
    ]
  )

  const stop = useCallback(async () => {
    stopRequestedRef.current = true

    if (deliverMinutesViaClientUpload) {
      await streamerRef.current?.stop()
      streamerRef.current = null
      const socket = wsRef.current
      if (socket) {
        socket.onmessage = null
        socket.onerror = null
        socket.onclose = null
        socket.close()
        wsRef.current = null
      }
      setState('idle')
      return
    }

    setState('stopping')

    // Flushed before `stop` goes out: ending the recorder emits one last chunk
    // holding everything since the previous timeslice, and telling the server
    // the meeting is over first drops the end of it.
    await streamerRef.current?.stop()
    streamerRef.current = null

    const socket = wsRef.current
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ event: 'stop' }))
      return
    }

    // Still connecting — nothing was recorded, so there is no job to queue.
    // Close any half-open socket and settle to error so End can finish without
    // waiting forever, and a late `ready` cannot start the streamer.
    if (socket) {
      socket.close()
      wsRef.current = null
    }
    fail(new Error('Internal meeting start cancelled'))
  }, [deliverMinutesViaClientUpload, fail])

  const pause = useCallback(() => {
    // No `pause` control message: the server drops audio that arrives while a
    // meeting is paused, so anything still streaming becomes a hole in the file.
    streamerRef.current?.pause()
    setState((current) => (current === 'recording' ? 'paused' : current))
  }, [])

  const resume = useCallback(() => {
    streamerRef.current?.resume()
    setState((current) => (current === 'paused' ? 'recording' : current))
  }, [])

  const sendSpeakerMap = useCallback((speakers: Record<string, string>) => {
    // Whole-file diarization labels people "1", "2"… and this is the only way
    // those become names. The job reads the map when it runs, after `stop`, so
    // any time before that is soon enough.
    const socket = wsRef.current
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ event: 'speaker.map', speakers }))
    }
  }, [])

  return { state, error, configProblem, queued, start, stop, pause, resume, sendSpeakerMap }
}
